import {
  Badge,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@tinct/web-components';
import { ReadScimToken } from '@tinct/core-api-models';
import { formatRelativeTime } from '@tinct/core-utils';
import { formatDate } from '../format-date';

const creatorName = (createdBy: ReadScimToken['createdBy']) => {
  if (!createdBy) return '—';
  const name = [createdBy.firstName, createdBy.lastName]
    .filter(Boolean)
    .join(' ');
  return name || createdBy.email;
};

export type ScimTokensTableProps = {
  tokens: ReadScimToken[];
  /** Admins revoke; without it the table is read-only. */
  onRevoke?: (token: ReadScimToken) => void;
};

/** The workspace's unrevoked SCIM tokens — two at most, so a plain table rather than a collection. */
export function ScimTokensTable({ tokens, onRevoke }: ScimTokensTableProps) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Created</TableHead>
          <TableHead>By</TableHead>
          <TableHead>Last used</TableHead>
          {onRevoke && (
            <TableHead className="text-right">
              <span className="sr-only">Actions</span>
            </TableHead>
          )}
        </TableRow>
      </TableHeader>
      <TableBody>
        {tokens.map((token) => (
          <TableRow key={token.id}>
            <TableCell>
              <div className="flex items-center gap-2">
                <span>{formatDate(token.createdAt)}</span>
                {token.unused && (
                  <Badge variant="warning">Unused for 90 days</Badge>
                )}
              </div>
            </TableCell>
            <TableCell>{creatorName(token.createdBy)}</TableCell>
            <TableCell>
              {token.lastUsedAt ? (
                <div className="flex flex-col">
                  <span>{formatRelativeTime(new Date(token.lastUsedAt))}</span>
                  {token.lastUsedIp && (
                    <span className="font-mono text-xs text-muted-foreground">
                      from {token.lastUsedIp}
                    </span>
                  )}
                </div>
              ) : (
                <span className="text-muted-foreground">Never</span>
              )}
            </TableCell>
            {onRevoke && (
              <TableCell className="text-right">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-destructive"
                  onClick={() => onRevoke(token)}
                >
                  Revoke
                </Button>
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
